import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, RefreshCw, Search, Trash2 } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function AdminDuplicates() {
  const { t } = useI18n();
  const [data, setData] = useState({ groups: [], total_media: 0, without_checksum: 0 });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const rescan = async () => {
    setScanning(true);
    try {
      const { data: job } = await api.post("/admin/duplicates/rescan");
      if (!job.id) { toast.success(t("dupes.upToDate")); setScanning(false); return; }
      const tick = setInterval(async () => {
        const { data: s } = await api.get(`/admin/photolab/jobs/${job.id}`);
        if (s.status !== "running") {
          clearInterval(tick);
          setScanning(false);
          toast.success(`${t("dupes.scanDone")} (${s.done}/${s.total})`);
          load();
        }
      }, 3000);
    } catch (err) {
      setScanning(false);
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/duplicates")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const keepOne = async (group, keep) => {
    const others = group.items.filter((i) => i.id !== keep.id);
    if (!window.confirm(`${t("dupes.keepConfirm")}\n\n${keep.filename}\n\n${t("dupes.willDelete")}: ${others.map((o) => o.filename).join(", ")}`)) return;
    try {
      await api.post("/admin/duplicates/keep", { keep_id: keep.id, delete_ids: others.map((o) => o.id) });
      toast.success(t("dupes.deleted"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`${item.filename}\n\n${t("dupes.confirm")}`)) return;
    try {
      await api.post("/admin/media/bulk", { media_ids: [item.id], delete: true });
      toast.success(t("dupes.deleted"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-duplicates">
      <div className="flex flex-wrap items-center gap-4">
        <p className="text-sm text-[#5A5752]">{t("dupes.help")}</p>        <button
          type="button"
          data-testid="dupes-refresh"
          onClick={load}
          className="inline-flex min-h-[42px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329]"
        >
          <RefreshCw size={13} /> {t("dupes.refresh")}
        </button>
        <button
          type="button"
          data-testid="dupes-rescan"
          disabled={scanning}
          onClick={rescan}
          className="inline-flex min-h-[42px] items-center gap-2 border border-[#8F3329] px-4 text-xs text-[#8F3329] disabled:opacity-50"
        >
          <Search size={13} /> {scanning ? t("dupes.scanning") : t("dupes.rescan")}
        </button>
      </div>

      <p className="text-[11px] text-[#5A5752]" data-testid="dupes-rescan-help">{t("dupes.rescanHelp")}</p>

      <p className="label-mono" data-testid="dupes-stats">
        {data.groups.length} {t("dupes.groups")} · {data.total_media} {t("dupes.mediaTotal")}
        {data.without_checksum ? ` · ${data.without_checksum} ${t("dupes.pending")}` : ""}
        {data.external_media ? ` · ${data.external_media} ${t("dupes.external")}` : ""}
      </p>

      {loading && <p className="text-sm text-[#5A5752]">{t("common.loading")}</p>}
      {!loading && data.groups.length === 0 && (
        <p className="text-sm text-[#5A5752]" data-testid="dupes-empty">{t("dupes.none")}</p>
      )}

      <div className="space-y-5">
        {data.groups.map((g) => (
          <div
            key={g.key}
            data-testid={`dupes-group-${g.key.slice(0, 8)}`}
            className="border border-[#C69A3B]/40 bg-white p-5"
          >
            <p className="label-mono">
              {g.count} {t("dupes.copies")} · {g.kind === "visual" ? t("dupes.visual") : t("dupes.exact")}
            </p>
            {g.kind === "visual" && (
              <p className="mt-1 text-[11px] text-[#8F3329]">{t("dupes.visualWarning")}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-4">
              {g.items.map((it, i) => (
                <div key={it.id} className="w-44">
                  <img
                    src={mediaUrl(it, "thumb")}
                    alt={it.filename}
                    className="h-32 w-44 border border-[#C69A3B]/30 object-cover"
                  />
                  <p className="mt-1 truncate text-xs text-[#252525]" title={it.filename}>{it.filename}</p>
                  <p className="text-[11px] text-[#5A5752]">
                    {it.size ? `${(it.size / 1048576).toFixed(1)} MB` : "—"}
                    {it.width ? ` · ${it.width}×${it.height}` : ""}
                  </p>
                  <p className="text-[11px] text-[#5A5752]">
                    {it.event_name_es} · {it.album_name_es}
                  </p>
                  <button
                    type="button"
                    data-testid={`dupes-keep-${it.id}`}
                    onClick={() => keepOne(g, it)}
                    className="mt-1 inline-flex min-h-[36px] w-full items-center justify-center gap-1 border border-[#275E64] text-[11px] text-[#275E64] transition-colors duration-300 hover:bg-[#275E64] hover:text-[#FAFAF7]"
                  >
                    <Check size={12} /> {t("dupes.keepThis")}
                  </button>
                  <button
                    type="button"
                    data-testid={`dupes-delete-${it.id}`}
                    onClick={() => remove(it)}
                    className="mt-1 inline-flex min-h-[36px] w-full items-center justify-center gap-1 border border-[#8F3329] text-[11px] text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                  >
                    <Trash2 size={12} /> {t("btn.delete")}
                  </button>
                  {i === 0 && (
                    <p className="mt-1 text-[11px] text-[#C69A3B]">{t("dupes.biggest")}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
